<template lang="html">
  <section class="user-action" tabindex="0" @keydown.enter.stop="onActionClick">
    <div class="action-link">
      <chevrons :is-open="isOpen" @on-chevon-click="onChevronClick" />
      <div @click="onActionClick" class="actions-label">
        {{ totalUserActionsLabel }}:
      </div>
    </div>
    <div class="selection-items" v-if="isOpen">
      <div v-if="isLoading">
        <transition name="loader">
          <div class="loader"></div>
        </transition>
      </div>
      <div v-else>
        <div class="action-row">
          <div class="label">
            {{ getMessageText(messages.NAVIGATION_ACTION) }}:
          </div>
          <div class="selection">
            <b-form-select
              v-model="selectedAction"
              :options="navigationActions"
              @change="onSelectedAction"
              size="sm"
            />
          </div>
        </div>
        <div v-if="showRecordId">
          <div class="action-row">
            <div class="label">{{ getMessageText(messages.RECORD_ID) }}:</div>
            <div class="selection">
              <expressions
                :data-elements="allSources"
                :selected-source="selectedSource"
                :data-type="dataType"
                :initial-expression="selectedExpression"
                :is-global="isGlobal"
                :content-type="currentApp.type"
                @on-expression-field-valid="onSelectedExpression"
                @on-object-tags-found="onObjectTagsFound"
              />
            </div>
          </div>
        </div>
        <div v-if="showMyInsightsTab">
          <div class="action-row">
            <div class="label">{{ $t("myInsightsTabs") }}:</div>
            <div class="selection">
              <draggable-item
                class="tab-row"
                v-for="(val, index) in selectedTargets"
                :key="index"
                :id="index"
                :info="val"
                @on-draggable-drop="onDraggableDrop"
                @on-draggable-drag-start="onDraggableStart"
              >
                <search-box-simple
                  :id="index.toString()"
                  :list="contentOptions"
                  :value="selectedTargets[index]"
                  :has-icon="false"
                  @on-search-box-item-selected="onSelectedTarget"
                >
                </search-box-simple>
                <div class="delete-wrapper">
                  <svg
                    class="delete-row-icon"
                    tabindex="0"
                    @keydown.enter.stop="enableDeleteModal(index)"
                    @click="enableDeleteModal(index)"
                    v-if="selectedTargets.length > 1"
                  >
                    <use
                      xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#delete"
                    ></use>
                  </svg>
                </div>
                <div ref="deleteColumnModal">
                  <simple-modal
                    ref="simpleModal"
                    :show="displayDeleteModal"
                    :message="getMessageText(messages.ARE_YOU_SURE)"
                    :cancel-text="getMessageText(messages.CANCEL)"
                    :ok-text="getMessageText(messages.OK)"
                    @on-simple-modal-cancel="disableDeleteModal"
                    @on-simple-modal-ok="deleteTarget"
                  />
                </div>
              </draggable-item>
              <button class="m-button neutral" @click="addField">
                {{ $t("add") }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script lang="js">
  import store from "@/store";
  import Chevrons from "@/views/drag-drop-pages/components/Chevrons";
  import Expressions from "@/components/Expressions";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import SimpleModal from "@/views/drag-drop-pages/components/modal/SimpleModal";
  import DraggableItem from "@/views/drag-drop-pages/components/list/DraggableItem";
  import sortBy from "lodash/sortBy";
  import {CONTENT_TYPES, NAVIGATION_ACTIONS} from "@/static/global-variables";
  import utils from "@/utils";

  export default  {
    name: "user-action",
    components: {
      SearchBoxSimple,
      Chevrons,
      Expressions,
      SimpleModal,
      DraggableItem
    },
    props: {
      id: {
        type: String
      },
      allSources: {
        type: Object
      },
      selectedSource: {
        type: Object
      },
      action: {
        type: Object
      },
      dataType: {
        type: String
      },
      isGlobal: {
        type: Boolean,
        default: false
      },
      appId : {
        type: String
      }
    },
    computed: {
      appType() {
        const matchedAppOnServer = utils.findAppByAppId(
            store.getters.apps,
            this.appId
        );
        return matchedAppOnServer.type;
      },
      messages() {
        return store.state.messages;
      },
      apps() {
        return store.state.apps;
      },
      currentApp() {
        return store.state.currentContent;
      },
      contentOptions() {
        const targetMap = new Map(this.selectedTargets.map((obj) => [obj.uuid, obj]));
        const filterApps = this.apps.filter(element => !targetMap.has(element.uuid));

        let filteredArray;
        if(this.selectedAction === NAVIGATION_ACTIONS.VIEW_SECTION) {
          filteredArray = filterApps.filter(element => element.type === this.currentApp.type).map(e =>
          {
            return this.createSearchBoxEntry(e);
          });
        } else {
          filteredArray = filterApps.filter(element => element.type === CONTENT_TYPES.ACCOUNT).map(e =>
          {
            return this.createSearchBoxEntry(e);
          });
        }
        // sort list with content first, then templates
        filteredArray.sort((x, y) => Number(x.isTemplate) - Number(y.isTemplate));
        return filteredArray;
      },
      showRecordId() {
        return this.selectedAction
          && (this.selectedAction === NAVIGATION_ACTIONS.VIEW_RECORD || this.selectedAction === NAVIGATION_ACTIONS.EDIT_RECORD);
      },
      totalUserActionsLabel() {
        return this.replaceMessageText(this.messages.VIEW_USER_ACTIONS, this.totalActions);
      },
      totalActions() {
        return  this.userActionCount + this.dataActionsCount;
      },
      userActionCount() {
        // For now, the number will always be 0 or 1, because they can only add the one Navigation Action.
        return this.isValidUserAction? 1 : 0;
      },
      dataActionsCount() {
        // Eventually, they will be able to add Data Actions, as well.
        // Add logic to count total Data Actions when ready.
        return 0;
      },
      isValidUserAction() {
        if (!this.userAction || !this.userAction.navigation.type) {
          return false;
        }
        if ((this.userAction.navigation.type === NAVIGATION_ACTIONS.VIEW_RECORD
            || this.userAction.navigation.type === NAVIGATION_ACTIONS.EDIT_RECORD)
            && this.userAction.navigation.recordId) {
          return true;
        }
        return this.userAction.navigation.type === NAVIGATION_ACTIONS.VIEW_SECTION && this.userAction.navigation.target
            && this.userAction.navigation.target.length > 0;

      },
      showMyInsightsTab() {
        return (this.selectedAction === NAVIGATION_ACTIONS.VIEW_SECTION) || (this.selectedAction === NAVIGATION_ACTIONS.VIEW_RECORD && this.objectTags.includes("Account"))
      }
    },
    mounted () {
      this.buildNavigationAction();
      this.userAction = this.action;
      this.isLoading = true;
      this.setup();
    },
    data () {
      return {
        isOpen: false,
        isLoading: false,
        userAction: null,
        navigationActions: [],
        selectedAction: null,
        selectedExpression: null,
        selectedTargets: [{
          isEmpty: true,
          rowId: 0
        }],
        displayDeleteModal: false,
        toDeleteTarget: null,
        objectTags: []
      }
    },
    methods: {
      setup() {
        if(this.action && this.action.navigation) {
          this.selectedAction = this.action.navigation.type;
        }
        if(this.action && this.action.navigation.recordId) {
          this.selectedExpression = this.action.navigation.recordId;
        }
        if(this.action && this.action.navigation.target && this.action.navigation.target.length > 0) {
          this.selectedTargets = this.formatTargetsForStudio(this.action.navigation.target);
        }
        this.isLoading = false;
        this.triggerEvent();
      },
      buildNavigationAction() {
        // add empty option to allow delete user action
        this.navigationActions = [{
          value: null,
          text: ""
        }];

        if (this.currentApp.type === CONTENT_TYPES.CALL) {
          // Add Edit Record navigation for Call app types
          this.navigationActions.push({
            value: NAVIGATION_ACTIONS.EDIT_RECORD,
            text: this.getMessageText(this.messages.EDIT_RECORD)
          });
        } else {
          // Add View Record navigation for non-Call app types
          this.navigationActions.push({
            value: NAVIGATION_ACTIONS.VIEW_RECORD,
            text: this.getMessageText(this.messages.VIEW_RECORD)
          });
        }

        // add view section navigation
        if(this.currentApp.type === CONTENT_TYPES.ACCOUNT || this.currentApp.type === CONTENT_TYPES.TERRITORY) {
          this.navigationActions.push({
            value: NAVIGATION_ACTIONS.VIEW_SECTION,
            text: this.$t("viewSection")
          });
        }
      },
      onActionClick() {
        this.isOpen = !this.isOpen;
        this.cleanupActions();
      },
      onChevronClick(status) {
        this.isOpen = status.open;
        this.cleanupActions();
      },
      cleanupActions() {
        if(!this.isOpen && !this.isValidUserAction) {
          this.resetSelectedUserAction();
          this.triggerEvent();
        }
      },
      resetSelectedUserAction() {
        this.selectedAction = null;
        this.selectedExpression = null;
        this.selectedTargets = [];
        this.addField();
      },
      addField() {
        const newTab = {
          isEmpty: true,
          rowId: this.selectedTargets.length
        };
        this.selectedTargets.push(newTab);
        this.triggerEvent();
      },
      onSelectedAction() {
        this.selectedExpression = null;
        this.selectedTargets = [];
        this.addField();
        this.triggerEvent();
      },
      onSelectedExpression(ctx) {
        this.selectedExpression = ctx.status? ctx.confExpression : null;
        if(!this.selectedExpression) {
          this.onObjectTagsFound([]);
        }
        this.triggerEvent();
      },
      onObjectTagsFound(objectTags) {
        this.objectTags = objectTags;
      },
      onSelectedTarget(ctx) {
        let idx = parseInt(ctx.searchBoxId);
        // map used to recompute available options for content
        this.selectedTargets = this.selectedTargets.map((elem, index) => {
          if(index === idx) {
            ctx.rowId = idx;
            return ctx;
          }
          return elem;
        });
        this.triggerEvent();
      },
      createSearchBoxEntry(element, rowId){
        let entry = {
          label: element.name,
          type: element.type,
          uuid: element.uuid,
          isEmpty: false,
          isTemplate: element.isTemplate
        }
        if(rowId){
          entry.rowId = rowId;
        }
        return entry;
      },
      triggerEvent() {
        this.userAction = null;
        if(this.selectedAction) {
          this.userAction = {
            navigation: {
              type: this.selectedAction,
              recordId: this.selectedExpression,
              target: this.formatTargetsForConfig()
            },
            dataActions: []
          };
        }
        this.$emit("on-user-action-change", this.userAction);
      },
      formatTargetsForConfig(){
        // remove emtpy nav targets for view record since they are optional
        const filteredTargets = (this.selectedAction === NAVIGATION_ACTIONS.VIEW_RECORD || this.selectedAction === NAVIGATION_ACTIONS.EDIT_RECORD) ? this.selectedTargets.filter(e => !e.isEmpty) : this.selectedTargets;
        return filteredTargets.map(e => {
          return {
            Studio_Id_vod__c : e.uuid
          }
        });
      },
      formatTargetsForStudio(configTargets){
        // format targets from config to match vue component
        const appMap = new Map(this.apps.map((obj) => [obj.uuid, obj]));
        return configTargets.map((e, idx) => {
          if(appMap.has(e.Studio_Id_vod__c)) {
            return this.createSearchBoxEntry(appMap.get(e.Studio_Id_vod__c),idx);
          } else {
            return {
              label: this.$t("targetNotFound"),
              uuid: e.Studio_Id_vod__c,
              isEmpty: false,
              rowId: idx
            };
          }
        });
      },
      enableDeleteModal(id) {
        this.displayDeleteModal = true;
        this.toDeleteTarget = id;
      },
      disableDeleteModal() {
        this.displayDeleteModal = false;
        this.toDeleteTarget = null;
      },
      deleteTarget() {
        this.selectedTargets = this.selectedTargets.filter((element, index) => { return this.toDeleteTarget !== index });
        this.triggerEvent();
        this.disableDeleteModal();
      },
      onDraggableStart(ev) {
        this.from = ev.data.info;
      },
      onDraggableDrop(ev) {
        const from = this.from;
        const to = ev.data.info;
        if(from.rowId !== to.rowId) {
          if(to.rowId < from.rowId) {
            this.moveDown(from, to);
          } else {
            this.moveUp(from, to);
          }
          const rows = sortBy(this.selectedTargets, "rowId", "asc");
          this.selectedTargets = [];
          this.$nextTick(() => {
            this.selectedTargets = rows;
            this.triggerEvent();
          });

        }
      },
      moveDown(from, to) {
        let tmpRowId = 0;
        for(const row of this.selectedTargets) {
          if(row.uuid === to.uuid) {
            from.rowId = tmpRowId++;
            row.rowId = tmpRowId++;
          } else if(row.uuid !== from.uuid) {
            row.rowId = tmpRowId++;
          }
        }
      },
      moveUp(from, to) {
        const length = this.selectedTargets.length;
        let tmpRowId = length - 1;
        for(let i = length - 1; i > -1; i--) {
          const row = this.selectedTargets[i];
          if(row.uuid === to.uuid) {
            from.rowId = tmpRowId--;
            row.rowId = tmpRowId--;
          } else if(row.uuid !== from.uuid) {
            row.rowId = tmpRowId--;
          }
        }
      }
    }
  }
</script>

<style scoped lang="scss">
  .user-action {
    box-sizing: border-box;
    .action-link {
      display: flex;
      align-items: center;
      cursor: pointer;
      font-size: 0.75rem;
      color: var(--BTN_BG_BLUE);
    }
    .selection-items {
      margin-top: 0.3rem;
    }
    .action-row {
      display: flex;
      flex-direction: column;
      margin-bottom: 0.5rem;
      .label {
        padding-top: 0.3rem;
        flex-basis: 1.75rem;
      }
      .selection {
        width: 100%;
        padding: 0 0.5rem;
      }
    }
    .tab-row {
      display: flex;
      align-items: center;
      padding-bottom: 1rem;
    }
    .actions-label {
      padding-left: 0.3rem;
    }
    .loader {
      margin-left: 1rem;
      width: 25px;
      height: 25px;
    }
    .delete-row-icon {
      margin-left: 0.25rem;
      height: 1rem;
      width: 1rem;
      fill: #c23934;
      cursor: pointer;
    }
  }
</style>
